import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Loader2 } from 'lucide-react';
import { useNotesLock } from '@/hooks/useNotesLock';
import { SetupNotesLockModal } from '@/components/notes/SetupNotesLockModal';
import { notifications } from '@/utils/notifications';

export function NotesLockSettings() {
  const { t } = useTranslation('settings');
  const notesLock = useNotesLock();
  const [setupOpen, setSetupOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const handleReset = async () => {
    if (!window.confirm(t('notesLock.resetConfirm'))) return;
    setResetBusy(true);
    try {
      await notesLock.resetPassword();
      notifications.success(t('notesLock.resetSuccess'));
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : t('notesLock.errors.resetFailed'));
    } finally {
      setResetBusy(false);
    }
  };

  if (notesLock.loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="animate-spin" size={18} />
        {t('notesLock.loading')}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('notesLock.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('notesLock.pageDescription')}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-2 text-primary">
            <Lock size={22} />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-foreground">
              {notesLock.config.configured ? t('notesLock.configured') : t('notesLock.notConfigured')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{t('notesLock.hint')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {!notesLock.config.configured ? (
                <button
                  type="button"
                  onClick={() => setSetupOpen(true)}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
                >
                  {t('notesLock.setPassword')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setChangeOpen(true)}
                    className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted/50"
                  >
                    {t('notesLock.changePassword')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleReset()}
                    disabled={resetBusy}
                    className="rounded-lg border border-red-500/40 px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                  >
                    {resetBusy ? t('notesLock.resetting') : t('notesLock.resetPassword')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <SetupNotesLockModal
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onSubmit={async (password, confirm) => {
          await notesLock.setupPassword(password, confirm);
          notifications.success(t('notesLock.setupSuccess'));
        }}
      />
      <SetupNotesLockModal
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        mode="change"
        onSubmit={async (current, newPassword) => {
          await notesLock.changePassword(current, newPassword, newPassword);
          notifications.success(t('notesLock.changeSuccess'));
        }}
      />
    </div>
  );
}
